import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { LogIn, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const goToGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
    const nav = useNavigate();
    const loc = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const from = loc.state?.from || "/";

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            await login(email.trim(), password, { rememberMe });
            toast.success("Benvenuto");
            nav(from, { replace: true });
        } catch (ex) {
            const msg = formatApiErrorDetail(ex?.response?.data?.detail) || ex.message;
            setErr(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell title="Accedi" subtitle="Bentornato in Capitaly">
            <button
                data-testid="google-signin-btn"
                type="button"
                onClick={goToGoogle}
                className="w-full flex items-center justify-center gap-2 border border-soft bg-elev2 px-4 py-2.5 text-sm font-semibold mb-4 hover:bg-elev"
            >
                <GoogleIcon /> Accedi con Google
            </button>
            <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-soft" />
                <span className="text-[10px] text-mute tracking-widest">OPPURE</span>
                <div className="flex-1 h-px bg-soft" />
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="login-form">
                <Field icon={Mail} type="email" placeholder="email@capitaly.app" value={email} onChange={setEmail} testid="email" autoComplete="email" />
                <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="password" autoComplete="current-password" />
                <RememberMeCheckbox checked={rememberMe} onChange={setRememberMe} testid="login-remember" />
                {err && <div data-testid="login-error" className="text-[11px] text-red-down border border-red px-2 py-1.5" style={{ color: "var(--red)", borderColor: "var(--red)" }}>{err}</div>}
                <button
                    data-testid="login-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full px-4 py-2.5 text-sm font-semibold tracking-wider disabled:opacity-50"
                    style={{ background: "var(--brand-blue)", color: "#000" }}
                >
                    {busy ? "Accesso in corso…" : <><LogIn size={14} className="inline mr-2" />Accedi</>}
                </button>
            </form>
            <div className="text-center text-xs text-mute mt-4">
                Non hai un account?{" "}
                <Link data-testid="link-register" to="/register" style={{ color: "var(--brand-green)" }} className="font-semibold hover:underline">
                    Registrati
                </Link>
            </div>
        </AuthShell>
    );
}

export const RememberMeCheckbox = ({ checked, onChange, testid }) => (
    <label className="flex items-center gap-2 text-xs text-mute cursor-pointer select-none">
        <input
            data-testid={testid}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="w-3.5 h-3.5 accent-current cursor-pointer"
            style={{ accentColor: "var(--brand-green)" }}
        />
        <span>Resta connesso per 30 giorni</span>
    </label>
);

export const AuthShell = ({ title, subtitle, children }) => (
    <div className="min-h-screen flex items-center justify-center px-4 fade-up">
        <div className="w-full max-w-md">
            <div className="text-center mb-8">
                <img src="/capitaly-icon.png" alt="Capitaly" width={56} height={56} className="mx-auto mb-3" />
                <h1 className="font-display text-3xl font-bold tracking-tight brand-gradient-text">{title}</h1>
                {subtitle && <p className="text-mute text-sm mt-1">{subtitle}</p>}
            </div>
            <div className="border border-soft bg-elev p-6">{children}</div>
        </div>
    </div>
);

export const Field = ({ icon: Icon, type, placeholder, value, onChange, testid, autoComplete }) => (
    <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute" />}
        <input
            data-testid={`field-${testid}`}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            required
            className="w-full bg-elev2 border border-soft pl-9 pr-3 py-2.5 text-sm focus:border-amber outline-none"
        />
    </div>
);

const GoogleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09A6.97 6.97 0 015.46 12c0-.73.13-1.43.36-2.09V7.07H2.18A11 11 0 001 12c0 1.78.43 3.46 1.18 4.93l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
);
